import React from "react"

import ChatInput from "~components/shared/chat/chat-input"
import ChatMessages from "~components/shared/chat/chat-messages"
import { ChatProvider } from "~components/shared/chat/chat-provider"
import { OUTFITTER_ASSISTANT_AGENT } from "~components/outfitter-assistant/outfitter-assistant-constants"
import AssistantQuickSuggestions from "~components/outfitter-assistant/assistant-quick-suggestions"
import { useOutfitterAssistant } from "~contexts/outfitter-assistant-provider"
import useOutfitterAssistantSuggestions from "~utils/hooks/use-outfitter-assistant-suggestions"
import { componentStylesCss } from "~utils/helpers/style-helpers"
import COPY from "~config/copy-constants"

// The assistant uses ChatProvider's suggestion callbacks, but has no
// suggestion flow of its own — share a stable no-op reference instead of
// allocating a new function (or useCallback) per render.
const NO_OP_SUGGESTION_HANDLER = () => {}

export default function AssistantChatContent() {
    const {
        assistantContext,
        chat: { messages, sendMessage, retry, isLoading, error },
    } = useOutfitterAssistant()
    const styles = useStyles()
    const shouldShowEmptyState = !messages.length && !error
    const {
        suggestions,
        isLoading: areSuggestionsLoading,
        error: suggestionsError,
    } = useOutfitterAssistantSuggestions({
        assistantContext,
        shouldSkip: !shouldShowEmptyState,
    })

    const handleSelectSuggestion = React.useCallback(
        (suggestion: string) => {
            void sendMessage({ text: suggestion })
        },
        [sendMessage]
    )

    const handleSendUserMessage = React.useCallback(
        (text: string) => {
            void sendMessage({ text })
        },
        [sendMessage]
    )

    return (
        <ChatProvider
            aiAgent={OUTFITTER_ASSISTANT_AGENT}
            selectedAiSuggestionId={null}
            setSelectedAiSuggestionId={NO_OP_SUGGESTION_HANDLER}
            lastSelectedAiSuggestionId={null}
            setLastSelectedAiSuggestionId={NO_OP_SUGGESTION_HANDLER}
            acceptSuggestion={NO_OP_SUGGESTION_HANDLER}
            cancelSuggestion={NO_OP_SUGGESTION_HANDLER}
            isLoadingAcceptSuggestion={false}
            sendUserMessage={handleSendUserMessage}
            isChatLoading={isLoading}
        >
            <div style={styles.messagesWrapper}>
                <ChatMessages
                    messages={messages}
                    isLoading={isLoading}
                    error={error}
                    onRetry={retry}
                    shouldAutoPreviewSuggestions={false}
                    emptyStateContent={
                        <AssistantQuickSuggestions
                            suggestions={suggestionsError ? [] : suggestions}
                            isLoading={areSuggestionsLoading}
                            onSelect={handleSelectSuggestion}
                        />
                    }
                />
            </div>
            <ChatInput
                sendMessage={sendMessage}
                isLoading={isLoading}
                placeholder={COPY.OUTFITTER_ASSISTANT_INPUT_PLACEHOLDER}
            />
        </ChatProvider>
    )
}

function useStyles() {
    // Gives <ChatMessages /> a bounded flex column so its overflow: auto
    // scrolls.
    return componentStylesCss({
        messagesWrapper: {
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            flex: 1,
        },
    })
}
